import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { doDeleteOrders, doGetOrders } from '../modules/orders/stores/thunk';
import { selectAllOrders } from '../modules/orders/stores/slice';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DATE_BACKEND_FORMAT, ORDER_STATUSES, PAGE_SIZE } from '../constants';
import OrdersTable from '../components/Tables/OrdersTable';
import { Badge, Button, Input, message, Popconfirm, Popover } from 'antd';
import { ReactSVG } from 'react-svg';
import queryString from 'query-string';
import OrdersFilter from '../components/Forms/Filters/OrdersFilter';
import moment from 'moment';
import { socket } from '../utils/socket';

// Reverse of MAP_CSV_DATA in UploadOrders.jsx — maps API field → CSV header
const CSV_FIELD_MAP = [
  ['orderRef', 'Order Ref'],
  ['deliveryDate', 'Delivery Date'],
  ['load', 'Load'],
  ['name', 'Name'],
  ['description', 'Description'],
  ['sizeLength', 'Length(cm)'],
  ['sizeWidth', 'Width(cm)'],
  ['sizeHeight', 'Height(cm)'],
  ['weight', 'Weight(kg)'],
  ['pickupName', 'Pickup Name'],
  ['pickupAddress', 'Pickup Address'],
  ['pickupAddressLat', 'Pickup Address Latitude'],
  ['pickupAddressLng', 'Pickup Address Longitude'],
  ['pickupTimeStart', 'Pickup Time Start'],
  ['pickupTimeEnd', 'Pickup Time End'],
  ['pickupPhone', 'Pickup Phone'],
  ['pickupNote', 'Pickup Note'],
  ['pickupDuration', 'Pickup Duration'],
  ['deliveryName', 'Delivery Name'],
  ['deliveryEmail', 'Delivery Email'],
  ['deliveryAddress', 'Delivery Address'],
  ['deliveryAddressLat', 'Delivery Address Latitude'],
  ['deliveryAddressLng', 'Delivery Address Longitude'],
  ['deliveryPhone', 'Delivery Phone'],
  ['deliveryTimeStart', 'Delivery Time Start'],
  ['deliveryTimeEnd', 'Delivery Time End'],
  ['deliveryNote', 'Delivery Note'],
  ['deliveryDuration', 'Delivery Duration'],
  ['partner', 'Partner'],
  ['partnerContact', 'Partner Contact'],
  ['partnerOrderRef', 'Partner Order Ref'],
  ['tourRef', 'Tour Ref'],
  ['airtableOrderRecordRef', 'Airtable Order Record Ref'],
  ['preparationMode', 'Preparation Mode'],
  ['collectionForm', 'Collection Form'],
  ['types', 'Types'],
  ['volume', 'Volume'],
];

function escapeCsvField(value) {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

const OrdersPage = () => {
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('common');
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [messageApi, contextHolder] = message.useMessage();

  const { isFetching, metadata, action, status } = useSelector((state) => state.order);
  const orders = useSelector((state) => selectAllOrders(state));

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const tableConfigRef = useRef({});
  const [isActionOpen, setIsActionOpen] = useState(false);

  useEffect(() => {
    setSearchValue(query.q || '');
  }, []);

  const query = Object.fromEntries(searchParams);
  const currentPage = query.current || 1;
  const drawerPageSize = tableConfigRef.current?.pageSize;
  const itemsPerPage = query.pageSize || drawerPageSize || PAGE_SIZE;
  const pagination = {
    current: currentPage || 1,
    pageSize: itemsPerPage,
    locale: { items_per_page: tc('itemsPerPage', { entity: t('title') }) },
    total: metadata.totalItems || orders.length,
    showSizeChanger: false,
    showTotal: (total) => tc('totalItems', { total, entity: t('title') }),
  };

  const pushTo = (pushProps) => {
    navigate({
      pathname: pushProps.path || location.pathname,
      search: queryString.stringify(pushProps.query || {}, {
        skipNull: true,
      }),
      state: pushProps.state,
    });
  };

  useEffect(() => {
    dispatch(doGetOrders({ page: currentPage, take: itemsPerPage, ...query }));
  }, []);

  useEffect(() => {
    if (['deleteOrders']?.includes(action) && status === 'succeeded')
      dispatch(doGetOrders({ page: currentPage, take: itemsPerPage, ...query }));
  }, [action, status]);

  useEffect(() => {
    socket.connect();
    socket.on('orders', (data) => {
      if (data.event === 'orders.geocoding.completed') {
        dispatch(
          doGetOrders({
            currentPage,
            itemsPerPage,
            ...query,
          }),
        );
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [query]);

  const onSelectChange = (newSelectedRowKeys) => {
    if (newSelectedRowKeys.length === 0 && isActionOpen) setIsActionOpen(false);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleClickDeleteOrders = () => {
    if (isActionOpen) setIsActionOpen(false);
    const orderIdsRemove = orders
      .filter(
        (o) =>
          selectedRowKeys?.includes(o.orderCode) &&
          [ORDER_STATUSES.DRAFT, ORDER_STATUSES.GEOCODING, ORDER_STATUSES.ERROR, ORDER_STATUSES.UNASSIGNED]?.includes(
            o.status,
          ),
      )
      ?.map((o) => o.orderCode);

    if (orderIdsRemove.length !== selectedRowKeys.length) {
      const errorIds = [];
      selectedRowKeys.forEach((orderCode) => {
        if (!orderIdsRemove?.includes(orderCode)) errorIds.push(orderCode);
      });
      const orderCodesError = orders.filter((o) => errorIds?.includes(o.orderCode))?.map((o) => o.orderCode);
      messageApi.open({
        content: t('cannotDeleteOrders', { orderCodes: orderCodesError.join(', ') }),
        type: 'error',
        style: { marginTop: '52px' },
      });
    } else {
      dispatch(doDeleteOrders({ orderCodes: selectedRowKeys }));
    }
  };

  const handleExportOrders = () => {
    if (isActionOpen) setIsActionOpen(false);
    const selectedOrders = orders.filter((o) => selectedRowKeys?.includes(o.orderCode));
    if (!selectedOrders.length) return;

    const headers = CSV_FIELD_MAP.map(([, header]) => header);
    const rows = selectedOrders.map((order) =>
      CSV_FIELD_MAP.map(([field]) => {
        let value = order[field];
        if (field === 'deliveryDate' && value) {
          value = moment(value).format('DD-MM-YYYY');
        }
        if (field === 'types' && Array.isArray(value)) {
          value = value.join(',');
        }
        return escapeCsvField(value);
      }),
    );

    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `orders-export-${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    messageApi.open({
      type: 'success',
      style: { marginTop: '52px' },
      content: t('exportSuccess', { count: selectedOrders.length }),
    });
  };

  const contentPopoverAction = () => {
    return (
      <div className="buttons">
        <Button type="link" onClick={handleExportOrders}>
          {t('exportCsv')}
        </Button>
        <Popconfirm
          title={tc('areYouSureDelete')}
          onConfirm={() => handleClickDeleteOrders()}
          okText={tc('yes')}
          cancelText={tc('no')}
        >
          <Button type="link">{tc('delete')}</Button>
        </Popconfirm>
      </div>
    );
  };

  const onClickAction = () => {
    selectedRowKeys.length > 0 && setIsActionOpen(!isActionOpen);
  };

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    if (updatePath) {
      pushTo({ query: params });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetOrders({
        page: params.current || newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const handleTableChange = (paginationParams, _filters, sorter) => {
    const newParams = { ...query, ...paginationParams };

    if (sorter && sorter.field && sorter.order) {
      newParams.sortField = sorter.field;
      newParams.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else {
      delete newParams.sortField;
      delete newParams.sortOrder;
    }

    handleFilter(newParams, true);
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  const DELETABLE_STATUSES = [
    ORDER_STATUSES.DRAFT,
    ORDER_STATUSES.GEOCODING,
    ORDER_STATUSES.ERROR,
    ORDER_STATUSES.UNASSIGNED,
  ];

  const handleDeleteOrder = async (record) => {
    try {
      await dispatch(doDeleteOrders({ orderCodes: [record.orderCode] })).unwrap();
      message.success(t('orderDeleted', { orderCode: record.orderCode }));
      dispatch(doGetOrders({ page: currentPage, take: itemsPerPage, ...query }));
    } catch (error) {
      message.error(error?.message || t('failedToDelete'));
    }
  };

  const withAction = (record) => {
    const isDeletable = DELETABLE_STATUSES.includes(record.status);
    return (
      <div className="actions">
        {isDeletable ? (
          <Popconfirm
            title={t('deleteOrder')}
            description={t('deleteOrderConfirm', { orderCode: record.orderCode })}
            onConfirm={() => handleDeleteOrder(record)}
            okText={tc('delete')}
            cancelText={tc('cancel')}
            okButtonProps={{ danger: true }}
          >
            <Button type="link" className="more">
              <ReactSVG className="icon" src="/icons/more.svg" />
            </Button>
          </Popconfirm>
        ) : (
          <Button type="link" className="more" disabled>
            <ReactSVG className="icon" src="/icons/more.svg" />
          </Button>
        )}
        {![ORDER_STATUSES.DRAFT, ORDER_STATUSES.GEOCODING, ORDER_STATUSES.ERROR].includes(record.status) && (
          <Link to={`/admin/route-plans?date=${moment(record.deliveryDate).format(DATE_BACKEND_FORMAT)}`}>
            <Button type="link" className="more">
              <ReactSVG className="icon" src="/icons/route-plans.svg" />
            </Button>
          </Link>
        )}
      </div>
    );
  };

  const activeFilterCount = [
    query.name,
    query.routeId,
    query.partnerOrderRef,
    query.orderRef,
    query.status,
    query.deliveryDateFrom || query.deliveryDateTo,
    query.riderId,
    query.partnerId,
    query.orderType,
    query.issueTypes,
  ].filter(Boolean).length;

  return (
    <div className="OrdersPage Content">
      {contextHolder}
      <div className="ContentHeader">
        <div className="HeaderTitle">{t('title')}</div>
        <div className="HeaderToolBar">
          <div className="ToolBarButtons">
            <Link to="/admin/orders/new">
              <Button className="CreateButton ButtonPrimary" type="link" size="small">
                {t('addSingleOrder')}
                <ReactSVG className="icon" src="/icons/plus.svg" />
              </Button>
            </Link>
            <Link to="/admin/orders/upload">
              <Button className="CreateButton ButtonPrimary" type="primary" size="small">
                {t('uploadOrder')}
                <ReactSVG className="icon" src="/icons/upload.svg" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <div className="riders-header-table page-header-table">
        <div className="search">
          <Input
            onChange={handleChangeSearch}
            onBlur={handleSearch}
            placeholder={tc('search')}
            onKeyDown={handleKeyDown}
            suffix={<img src="/icons/search.svg" />}
            disabled={isFetching}
            value={searchValue}
          />
        </div>
        <div className="buttons">
          {selectedRowKeys.length > 0 && (
            <Popover
              open={selectedRowKeys?.length > 0 && isActionOpen}
              content={contentPopoverAction}
              overlayClassName="OrdersPagePopoverAction"
              placement="bottom"
            >
              <Button className="FilterButton ButtonFilter" onClick={onClickAction}>
                {selectedRowKeys?.length > 0 && <div className="count">{selectedRowKeys.length}</div>}
                {tc('action')} <ReactSVG className="icon" src="/icons/arrow_drop_down.svg" />
              </Button>
            </Popover>
          )}

          <Badge count={activeFilterCount} size="small" offset={[-5, 0]}>
            <Button className="FilterButton ButtonFilter" onClick={() => setIsFilterOpen(true)}>
              {tc('filter')}
              <ReactSVG className="icon" src="/icons/filter_list.svg" />
            </Button>
          </Badge>
          <Button
            type="text"
            onClick={() => tableConfigRef.current?.openDrawer?.()}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <ReactSVG className="icon" src="/icons/settings.svg" />
          </Button>
        </div>
      </div>
      <OrdersFilter open={isFilterOpen} onClose={() => setIsFilterOpen(false)} query={query} onApply={handleFilter} />
      <div className="ContentMain">
        <OrdersTable
          dataSource={orders}
          pagination={pagination}
          withAction={withAction}
          rowKey="orderCode"
          isFetching={false}
          onSelectChange={onSelectChange}
          onChange={handleTableChange}
          configRef={tableConfigRef}
          onPageSizeChange={(size) => {
            handleTableChange({ pageSize: size, current: 1 }, null, null);
          }}
        />
      </div>
    </div>
  );
};

export default OrdersPage;
